import type { AxiosInstance } from 'axios';

export interface DashboardStats {
  pendingVendors: number;
  pendingProducts: number;
  todayOrders: number;
  pendingPayouts: number;
  activeVendors: number;
}

/** One vendor's own overview. Every figure is counted in the database. */
export interface VendorDashboardSummary {
  store: { id: string; storeName: string; status: string };
  orders: {
    total: number;
    lifetimeSalesPaise: number;
    /** Cancelled orders are excluded from `orders`/`salesPaise` and reported separately. */
    thisMonth: {
      orders: number;
      salesPaise: number;
      newOrders: number;
      onDelivery: number;
      delivered: number;
      cancelled: number;
    };
    lastMonth: { orders: number; salesPaise: number };
    /** Last six calendar months, oldest first; `month` is `YYYY-MM`. */
    monthly?: Array<{ month: string; orders: number; salesPaise: number }>;
  };
  /** Products per category across the whole catalogue, largest first. */
  categories?: Array<{ name: string; count: number }>;
  shipments: {
    /** Keyed by every shipment status, zeros included — "0 undelivered" is an answer. */
    counts: Record<string, number>;
    recent: Array<{
      id: string;
      /** The order this parcel belongs to. */
      subOrderId: string;
      awbNumber: string | null;
      courierName: string | null;
      status: string;
      updatedAt: string;
      orderNumber: string;
    }>;
  };
}

/** A seller's store as a shopper sees it — nothing here is private. */
export interface PublicVendorStore {
  id: string;
  storeName: string;
  slug: string;
  status: string;
  /** The store's plan — what the storefront reads to mark a store as premium. */
  tier: string;
  description: string | null;
  /** The logo the seller set, or null (show the store's initial). */
  logoUrl: string | null;
  /** Products a shopper can find in the catalogue right now. */
  productCount: number;
  reviewCount: number;
  /** Average rating times 100, so no float crosses the wire. */
  avgRatingX100: number;
}

export interface VendorListItem {
  id: string;
  storeName: string;
  slug: string;
  status: string;
  tier: string;
  description?: string | null;
  gstin?: string | null;
  pickupStateCode?: string | null;
  pickupPincode?: string | null;
  duePaise: number;
  createdAt: string;
  user: {
    id: string;
    name: string;
    email: string;
    phone: string;
    kycStatus: string;
    emailVerified: boolean;
    kycRecord?: VendorKycReview | null;
  };
  _count: { products: number };
}

/** A seller's KYC as the approving admin sees it: identifiers masked, documents as links. */
export interface VendorKycReview {
  id: string;
  status: string;
  legalName: string;
  panMasked: string | null;
  gstin: string | null;
  bankAccountName: string;
  bankAccountMasked: string | null;
  bankIfsc: string;
  upiId: string | null;
  submittedAt: string;
  reviewedAt: string | null;
  reviewNote: string | null;
  gstVerification: {
    checkedAt: string;
    gstin: string;
    stateCode: string | null;
    stateName: string | null;
    offlineValid: boolean;
    portal: {
      found: boolean;
      status: string | null;
      active: boolean;
      legalName: string | null;
      tradeName: string | null;
      registeredOn: string | null;
      constitution: string | null;
      address: string | null;
      provider: string;
    } | null;
    portalError: string | null;
    nameMatches: boolean | null;
  } | null;
  panDocUrl: string | null;
  addressProofUrl: string | null;
  gstDocUrl: string | null;
}

/** One store under review: what blocks approval, and the last note sent to the seller. */
export interface VendorReview extends VendorListItem {
  approvalBlockers: string[];
  latestReviewNote: string | null;
  /** The pickup address and its Shiprocket status. */
  pickup?: VendorPickup;
}

export interface VendorListResponse {
  data: VendorListItem[];
  page: { page: number; pageSize: number; total: number; pageCount: number };
}

export interface VendorWarehouse {
  id: string;
  vendorId: string;
  name: string;
  line1: string;
  city: string;
  stateCode: string;
  pincode: string;
  line2?: string | null;
  landmark?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
  /** SYNCED | PENDING_VERIFICATION | FAILED, once registered as a pickup. */
  shiprocketStatus?: string | null;
  shiprocketError?: string | null;
  isDefault: boolean;
  createdAt: string;
}

/** Fields a seller sets on one of their warehouses. */
export interface VendorWarehouseInput {
  name: string;
  line1: string;
  line2?: string;
  landmark?: string;
  city: string;
  stateCode: string;
  pincode: string;
  contactName?: string;
  contactPhone?: string;
  isDefault?: boolean;
}

export interface VendorRating {
  id: string;
  storeName: string;
  slug: string;
  status: string;
  logoUrl: string | null;
  /** Products a shopper can find in the catalogue right now. */
  productCount: number;
  reviewCount: number;
  avgRatingX100: number;
}

/** A seller's pickup address and its pickup location at Shiprocket. */
export interface VendorPickup {
  warehouse: {
    id: string;
    line1: string;
    line2: string | null;
    landmark: string | null;
    city: string;
    stateCode: string;
    pincode: string;
    contactName: string | null;
    contactPhone: string | null;
  } | null;
  /** What is still missing before a courier could collect from it. */
  problems: string[];
  shiprocket: {
    code: string | null;
    status: 'SYNCED' | 'PENDING_VERIFICATION' | 'FAILED' | null;
    error: string | null;
    syncedAt: string | null;
  };
}

export interface VendorPickupInput {
  line1: string;
  line2?: string;
  landmark?: string;
  city: string;
  /** Two-letter state code, e.g. MP. */
  stateCode: string;
  pincode: string;
  contactName: string;
  contactPhone: string;
}

export function vendorEndpoints(http: AxiosInstance) {
  return {
    // ── Vendor self-service ──
    register: (body: {
      storeName: string;
      slug: string;
      description?: string;
      gstin?: string;
      pickupStateCode?: string;
      pickupPincode?: string;
    }) => http.post<VendorListItem>('/vendors/register', body).then((r) => r.data),

    getMyStore: () =>
      http
        .get<
          VendorListItem & {
            /** The reviewer's last word: why the store was sent back, rejected or suspended. */
            latestReviewNote: string | null;
            /** When the store was last suspended (ISO). */
            suspendedAt?: string | null;
            logoFileId: string | null;
            logoUrl: string | null;
          }
        >('/vendors/me')
        .then((r) => r.data),

    updateMyStore: (body: {
      storeName?: string;
      description?: string;
      gstin?: string;
      pickupStateCode?: string;
      pickupPincode?: string;
      /** A file registered through files.registerSelf; null removes the logo. */
      logoFileId?: string | null;
    }) => http.patch<VendorListItem>('/vendors/me', body).then((r) => r.data),

    getMyPickupAddress: () =>
      http.get<{ data: VendorPickup }>('/vendors/me/pickup-address').then((r) => r.data.data),

    setMyPickupAddress: (body: VendorPickupInput) =>
      http.put<{ data: VendorPickup }>('/vendors/me/pickup-address', body).then((r) => r.data.data),

    /** Admin: register (or re-check) a store's pickup at Shiprocket. */
    syncPickup: (vendorId: string) =>
      http
        .post<{ data: VendorPickup }>(`/admin/vendors/${vendorId}/pickup/sync`)
        .then((r) => r.data.data),

    /** Admin: register every live store's pickup. */
    syncAllPickups: () =>
      http
        .post<{
          data: {
            total: number;
            synced: number;
            pending: number;
            failed: number;
            results: Array<{
              vendorId: string;
              storeName: string;
              status: VendorPickup['shiprocket']['status'];
              error: string | null;
            }>;
          };
        }>('/admin/vendors/pickup/sync-all')
        .then((r) => r.data.data),

    submitForApproval: () =>
      http.post<{ id: string; status: string }>('/vendors/me/submit').then((r) => r.data),

    // ── vendor warehouses (self-service) ──
    listMyWarehouses: () =>
      http.get<{ data: VendorWarehouse[] }>('/vendors/me/warehouses').then((r) => r.data.data),

    createWarehouse: (body: VendorWarehouseInput) =>
      http.post<VendorWarehouse>('/vendors/me/warehouses', body).then((r) => r.data),

    updateWarehouse: (warehouseId: string, body: Partial<VendorWarehouseInput>) =>
      http
        .patch<VendorWarehouse>(`/vendors/me/warehouses/${warehouseId}`, body)
        .then((r) => r.data),

    /** Refused for the default warehouse and for one still holding stock. */
    deleteWarehouse: (warehouseId: string) =>
      http.delete<{ deleted: true }>(`/vendors/me/warehouses/${warehouseId}`).then((r) => r.data),

    // ── Admin endpoints ──
    /**
     * One approved seller's store, by the slug its page is addressed with.
     *
     * Public. Replaces resolving a slug by downloading the whole vendor
     * directory and searching it in the client.
     */
    getBySlug: (slug: string) =>
      http.get<{ data: PublicVendorStore }>(`/vendors/slug/${slug}`).then((r) => r.data.data),

    list: (params?: { page?: number; pageSize?: number; q?: string; status?: string }) =>
      http.get<VendorListResponse>('/vendors', { params }).then((r) => r.data),

    getById: (vendorId: string) =>
      http.get<VendorReview>(`/vendors/${vendorId}`).then((r) => r.data),

    transitionStatus: (vendorId: string, body: { status: string; note?: string }) =>
      http
        .patch<{ id: string; status: string }>(`/vendors/${vendorId}/status`, body)
        .then((r) => r.data),

    bulkTransitionStatus: (body: { ids: string[]; status: string; note?: string }) =>
      http
        .post<{
          successful: number;
          failed: number;
          errors: string[];
        }>(`/vendors/bulk-status`, body)
        .then((r) => r.data),

    create: (body: {
      name: string;
      email: string;
      phone: string;
      password: string;
      storeName: string;
      slug?: string;
    }) => http.post<{ id: string }>('/vendors', body).then((r) => r.data),

    ratings: () => http.get<{ data: VendorRating[] }>('/vendors/ratings').then((r) => r.data.data),

    getTimeline: (vendorId: string) =>
      http
        .get<{
          data: Array<{
            id: string;
            fromStatus: string | null;
            toStatus: string;
            actorId: string | null;
            note: string | null;
            createdAt: string;
          }>;
        }>(`/vendors/${vendorId}/timeline`)
        .then((r) => r.data),

    dashboardStats: () => http.get<DashboardStats>('/dashboard/stats').then((r) => r.data),

    /**
     * The calling vendor's own overview, counted server-side.
     *
     * Replaces adding up a page of sub-orders in the browser: `pageSize` caps at
     * 100, so that arithmetic quietly understated every figure for a seller with
     * more orders than that.
     */
    myDashboardSummary: () =>
      http.get<VendorDashboardSummary>('/vendor/dashboard/summary').then((r) => r.data),
  };
}

export type VendorEndpoints = ReturnType<typeof vendorEndpoints>;
